import { useEffect, useState } from 'react'
import {
    createSearchParams,
    useLocation,
    useNavigate,
    useSearchParams,
} from 'react-router-dom'
import { Button, Container, Stack, Tooltip, Typography } from '@mui/material'
import { Ear } from '../../../components/Ear'
import { LocationListItem } from '../../../components/LocationListItem'
import { useInnsynContext } from '../../../context/InnsynContextProvider'
import { INNSYN_SEARCH_PATH, INNSYN_SEARCH_RESULTS_PATH } from '../../../paths'
import { FormattedMessage, useIntl } from 'react-intl'
import NavigationHeader from '../../../components/NavigationHeader'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import colors from '../../../styles/colors.scss'

const positions = ['pos_1', 'pos_2', 'pos_3', 'pos_4', 'pos_5', 'pos_6']

interface TagSearchProps {
    state: {
        isRightEar?: boolean
    }
}

export const TagSearch = () => {
    const navigate = useNavigate()
    const intl = useIntl()
    const [query, setQuery] = useSearchParams()
    const { snitt } = useInnsynContext()
    const { state } = useLocation() as TagSearchProps
    const [ear, setEar] = useState<Snitt[]>()
    const [isRightEar, setIsRightEar] = useState<boolean>(
        state?.isRightEar !== undefined ? state.isRightEar : true
    )
    let xDown = null as null | number
    let yDown = null as null | number

    const getTouches = (e: TouchEvent) => {
        return e.touches
    }

    const handleTouchStart = (e: TouchEvent) => {
        const firstTouch = getTouches(e)[0]
        xDown = firstTouch.clientX
        yDown = firstTouch.clientY
    }

    const handleTouchMove = (e: TouchEvent) => {
        if (!xDown || !yDown) {
            return
        }
        let xUp = e.touches[0].clientX
        let yUp = e.touches[0].clientY
        let xDiff = xDown - xUp
        let yDiff = yDown - yUp

        if (Math.abs(xDiff) > Math.abs(yDiff)) {
            //right swipe
            if (xDiff > 0) {
                setIsRightEar(true)
            }
            //left swipe
            else {
                setIsRightEar(false)
            }
        }
    }
    useEffect(() => {
        document.addEventListener('touchstart', handleTouchStart, false)
        document.addEventListener('touchmove', handleTouchMove, false)
        return () => {
            document.removeEventListener('touchstart', handleTouchStart)
            document.removeEventListener('touchmove', handleTouchMove)
        }
    }, [])

    const getCut = (pos: number, id: number) => {
        const match = snitt
            ?.filter((el) => el.posisjon === pos)
            .filter((el) => el.id === id)[0]
        if (!match) {
            return undefined
        }
        return match
    }

    useEffect(() => {
        let cutBuilder = [] as Snitt[]

        positions.forEach((position, index) => {
            if (query.get(position)) {
                const cutId = query.get(position)
                if (cutId === null) return
                const parsedCutIdInt = parseInt(cutId)
                if (isNaN(parsedCutIdInt)) return
                const cut = getCut(index + 1, parsedCutIdInt)
                if (!cut) return
                cutBuilder = cutBuilder.concat(cut)
            }
        })
        setEar(cutBuilder)
    }, [query])

    const selectCut = (position: number, forceNavigateOnClick?: boolean) => {
        if (query.get(positions[position - 1]) && !forceNavigateOnClick) {
            query.delete(positions[position - 1])
            setQuery(query)
        } else {
            query.set('cut_pos', position.toString())
            setQuery(query)
            navigate(
                {
                    pathname: 'select-cut',
                    search: `?${createSearchParams(query)}`,
                },
                { state: { isRightEar: isRightEar } }
            )
        }
    }

    const search = () => {
        query.set('search_type', 'cut')
        navigate({
            pathname: INNSYN_SEARCH_RESULTS_PATH,
            search: `?${createSearchParams(query)}`,
        })
    }

    const reset = () => {
        navigate('/app/search/tag')
    }

    const getTitle = (rigtEar: boolean) => {
        return intl.formatMessage(
            { id: 'tag-search.swap-ear' },
            {
                side: intl.formatMessage({
                    id: rigtEar ? 'tag-search.right' : 'tag-search.left',
                }),
            }
        )
    }

    return (
        <Container sx={{ height: '100%' }}>
            <NavigationHeader
                onClickBack={() => navigate(INNSYN_SEARCH_PATH)}
                rightText='search-page.reset'
                onClickRightText={() => reset()}
            />
            <Stack
                justifyContent={'space-between'}
                sx={{ height: '90%', marginTop: '0.5rem' }}>
                <LocationListItem
                    pathname='select-area'
                    rightEar={isRightEar}
                />
                <Typography
                    sx={{
                        textAlign: 'center',
                        fontWeight: '300',
                        margin: '0.5rem',
                        fontSize: '0.875rem',
                    }}>
                    <FormattedMessage id='search-page.info-text' />
                </Typography>
                <Container>
                    <Ear
                        showToolTip
                        cuts={ear}
                        show={isRightEar ? 'right' : 'left'}
                        selectPosition={(positionKey) => selectCut(positionKey)}
                    />
                </Container>
                <Stack direction='row' sx={{ alignSelf: 'center' }}>
                    <Tooltip arrow title={getTitle(false)} placement='top'>
                        <ArrowBackIosNewIcon
                            onClick={() => setIsRightEar(false)}
                            fontSize='large'
                            aria-label={getTitle(false)}
                            sx={{
                                marginRight: '2.5rem',
                                marginTop: '1rem',
                                marginBottom: '1rem',
                                cursor: 'pointer',
                                stroke: isRightEar
                                    ? colors.blue
                                    : colors.iconGray,
                                strokeWidth: '1.2',
                                color: isRightEar
                                    ? colors.blue
                                    : colors.iconGray,
                                marginLeft: '1rem',
                            }}
                        />
                    </Tooltip>
                    <Tooltip arrow title={getTitle(true)} placement='top'>
                        <ArrowForwardIosIcon
                            onClick={() => setIsRightEar(true)}
                            fontSize='large'
                            aria-label={getTitle(true)}
                            sx={{
                                marginLeft: '2.5rem',
                                color: isRightEar
                                    ? colors.iconGray
                                    : colors.blue,
                                strokeWidth: '1.2',
                                stroke: isRightEar
                                    ? colors.iconGray
                                    : colors.blue,
                                marginTop: '1rem',
                                marginBottom: '1rem',
                                marginRight: '1rem',
                                cursor: 'pointer',
                            }}
                        />
                    </Tooltip>
                </Stack>
                <Button
                    onClick={() => search()}
                    size='medium'
                    variant='contained'>
                    <FormattedMessage id={'tag-search.search'} />
                </Button>
            </Stack>
        </Container>
    )
}

export default TagSearch
